import {
  CheckIcon,
  ChevronRightIcon,
  Rows01Icon,
  SearchLgIcon,
  XIcon,
} from '@langchain/untitled-ui-icons';

import { useState } from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/Popover';
import { Button, ButtonGroup, IconButton } from '@/design-system';
import { RunFacetField } from '@/types/schema';
import { cn } from '@/utils/tailwind';

import { useGroupByMetadata } from './useGroupByMetadata';
import { HIDDEN_BY_DEFAULT_METADATA_KEYS, getKeyName } from './utils';

type Props = {
  facets: RunFacetField[];
};

interface MetadataKeyButtonProps {
  metadataKey: RunFacetField;
  selectedKey: RunFacetField | null;
  onSelect: (key: RunFacetField | null) => void;
  isHidden?: boolean;
}

const MetadataKeyButton = ({
  metadataKey,
  selectedKey,
  onSelect,
  isHidden,
}: MetadataKeyButtonProps) => (
  <button
    type="button"
    onClick={() => onSelect(metadataKey === selectedKey ? null : metadataKey)}
    className={cn(
      'flex w-full justify-between rounded-md p-2 text-left font-semibold hover:bg-secondary',
      isHidden && 'pl-8 text-gray-500'
    )}
  >
    {getKeyName(metadataKey)}
    {selectedKey?.value === metadataKey.value && (
      <CheckIcon className="h-4 w-4" />
    )}
  </button>
);

export const GroupByHeaderButton = ({ facets }: Props) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const [isRunKeysExpanded, setIsRunKeysExpanded] = useState(true);
  const [isExampleKeysExpanded, setIsExampleKeysExpanded] = useState(true);
  const { metadataKey, setMetadataKey } = useGroupByMetadata();

  // First filter all metadata keys
  const allMetadataKeys = facets.filter(
    (facet) => facet.key === 'metadata_key'
  );

  // Split into visible and hidden lists
  const filteredKeys = allMetadataKeys
    .filter((key) => !HIDDEN_BY_DEFAULT_METADATA_KEYS.includes(key.value))
    .filter((key) => key.value.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.value.localeCompare(b.value));

  // Split into run and example metadata keys
  const runMetadataKeys = filteredKeys.filter(
    (key) => !key.value.startsWith('ls_example_')
  );

  const exampleMetadataKeys = filteredKeys.filter((key) =>
    key.value.startsWith('ls_example_')
  );

  const hasMetadataKeys = allMetadataKeys.length > 0;

  const handleSelect = (key: RunFacetField | null) => {
    setMetadataKey(key);
    setOpen(false);
  };

  const trigger = (
    <PopoverTrigger asChild>
      <Button
        color="secondary"
        variant="outlined"
        disabled={!hasMetadataKeys}
        leftDecorator={Rows01Icon}
        size="md"
      >
        {metadataKey ? getKeyName(metadataKey) : 'Group by'}
      </Button>
    </PopoverTrigger>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {metadataKey ? (
        <ButtonGroup color="secondary" size="md">
          {trigger}
          <IconButton
            label="Clear group by"
            icon={XIcon}
            onClick={() => setMetadataKey(null)}
          />
        </ButtonGroup>
      ) : (
        trigger
      )}

      <PopoverContent
        sideOffset={5}
        align="start"
        className="z-[999] flex flex-col p-0"
      >
        <div className="bg-secondary p-2 text-sm text-secondary">METADATA</div>

        <div className="flex flex-col">
          <div className="flex items-center gap-2 p-2">
            <SearchLgIcon className="h-4 w-4"></SearchLgIcon>
            <input
              className="w-full border-none bg-transparent p-0 text-sm outline-none"
              type="text"
              placeholder="Search by metadata key"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="h-px w-full bg-secondary"></div>

          {runMetadataKeys.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setIsRunKeysExpanded(!isRunKeysExpanded)}
                className="flex w-full items-center justify-between bg-secondary p-2 px-3 text-sm text-secondary hover:bg-tertiary"
              >
                <span>RUN KEYS</span>
                <ChevronRightIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isRunKeysExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {isRunKeysExpanded && (
                <div className="flex flex-col p-1 text-sm">
                  {runMetadataKeys.map((key) => (
                    <MetadataKeyButton
                      key={key.value}
                      metadataKey={key}
                      selectedKey={metadataKey}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}{' '}
            </>
          )}

          {exampleMetadataKeys.length > 0 && (
            <>
              <button
                type="button"
                onClick={() => setIsExampleKeysExpanded(!isExampleKeysExpanded)}
                className="flex w-full items-center justify-between bg-secondary p-2 px-3 text-sm text-secondary hover:bg-tertiary"
              >
                <span>EXAMPLE KEYS</span>
                <ChevronRightIcon
                  className={`h-4 w-4 transition-transform duration-200 ${
                    isExampleKeysExpanded ? 'rotate-90' : ''
                  }`}
                />
              </button>
              {isExampleKeysExpanded && (
                <div className="flex flex-col p-1 text-sm">
                  {exampleMetadataKeys.map((key) => (
                    <MetadataKeyButton
                      key={key.value}
                      metadataKey={key}
                      selectedKey={metadataKey}
                      onSelect={handleSelect}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {runMetadataKeys.length === 0 && exampleMetadataKeys.length === 0 && (
            <div className="p-2 text-sm text-secondary">
              No metadata keys found
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
